/**
 * TemplatesLanding
 *
 * Public landing section with static and video template marquees, including
 * modal preview for video templates and lazy fetch on demand.
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Link } from 'react-router-dom';
import { Play, X } from 'lucide-react';
import { getPublicVideoTemplates } from '@/api/content/videoTemplatesApi';

// Utility function to merge class names
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Images for the marquee (same as hero section)
const marqueeImages = [
  "/images/landing/hero1.png",
  "/images/landing/hero2.png",
  "/images/landing/hero3.png",
  "/images/landing/hero4.png",
  "/images/landing/hero5.png",
  "/images/landing/hero6.png",
  "/images/landing/hero7.png",
  "/images/landing/hero8.png",
  "/images/landing/hero9.png",
  "/images/landing/hero10.png",
  "/images/landing/hero11.png",
  "/images/landing/hero12.png",
  "/images/landing/hero13.png",
  "/images/landing/hero14.png",
  "/images/landing/hero15.png",
  "/images/landing/hero16.png",
  "/images/landing/hero17.png",
  "/images/landing/hero18.png",
  "/images/landing/hero19.png",
];

// Split the images array into two rows
const firstRow = marqueeImages.slice(0, Math.ceil(marqueeImages.length / 2));
const secondRow = marqueeImages.slice(Math.ceil(marqueeImages.length / 2));

// Template Card component
const TemplateCard = ({ image }) => {
  return (
    <figure
      className={cn(
        "relative h-48 w-48 sm:h-64 sm:w-64 mx-2 sm:mx-3 cursor-pointer overflow-hidden rounded-xl border",
        // light styles
        "border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05]",
        // dark styles
        "dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]",
      )}
    >
      <img 
        src={image} 
        alt="Template example" 
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </figure>
  );
};

// Video Template Card component with play button overlay
const VideoTemplateCard = ({ videoTemplate, onPlay }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Preload thumbnail image
  useEffect(() => {
    if (videoTemplate.thumbnailUrl) {
      const img = new Image();
      img.src = videoTemplate.thumbnailUrl;
      img.onload = () => {
        setImageLoaded(true);
      };
      img.onerror = () => {
        setThumbnailError(true);
        setImageLoaded(true);
      };
    } else {
      setImageLoaded(true);
    }
  }, [videoTemplate.thumbnailUrl]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handlePlayClick = (e) => {
    e.stopPropagation();
    onPlay(videoTemplate);
  };

  return (
    <figure
      className={cn(
        "relative h-48 w-48 sm:h-64 sm:w-64 mx-2 sm:mx-3 cursor-pointer overflow-hidden rounded-xl border",
        // light styles
        "border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05]",
        // dark styles
        "dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]",
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail image */}
      {!thumbnailError && videoTemplate.thumbnailUrl && (
        <img
          src={videoTemplate.thumbnailUrl}
          alt="Video template thumbnail"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      )}

      {/* Fallback when thumbnail fails */}
      {(thumbnailError || !videoTemplate.thumbnailUrl) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
          <div className="text-center">
            <div className="w-8 h-8 bg-gray-400 dark:bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-2">
              <Play className="w-4 h-4 text-white" />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Video Preview</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      )}

      {/* Play button overlay - only show on hover */}
      {isHovered && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 animate-in fade-in duration-200"
          onClick={handlePlayClick}
        >
          <div className="bg-white bg-opacity-95 rounded-full p-3 shadow-lg hover:bg-opacity-100 transition-all duration-200 transform hover:scale-110 animate-in zoom-in-95 duration-200">
            <Play className="w-8 h-8 text-gray-800" />
          </div>
        </div>
      )}
    </figure>
  );
};

// Video Skeleton Card Component
const VideoSkeletonCard = () => {
  return (
    <figure className="relative h-48 w-48 sm:h-64 sm:w-64 mx-2 sm:mx-3 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse">
        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse" />
      </div>
      {/* Play button skeleton with actual play icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 bg-white bg-opacity-90 dark:bg-gray-700 dark:bg-opacity-90 rounded-full flex items-center justify-center shadow-lg animate-pulse">
          <Play className="w-5 h-5 text-gray-400 dark:text-gray-500 ml-0.5" fill="currentColor" />
        </div>
      </div>
    </figure>
  );
};

// Toggle Button Component
const ToggleButton = ({ isVideo, onChange }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      <div className="relative flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 shadow-inner">
        <button
          onClick={() => onChange(false)}
          className={cn(
            "relative z-10 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
            !isVideo
              ? "bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          )}
        >
          Static Templates
        </button>
        <button
          onClick={() => onChange(true)}
          className={cn(
            "relative z-10 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
            isVideo
              ? "bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          )}
        >
          Video Templates
        </button>
      </div>
    </div>
  );
};

// Simple Marquee component
const Marquee = ({ children, reverse = false, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const marqueeRef = useRef(null);
  
  // Use Intersection Observer to only animate when visible - Safari optimization
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    
    if (marqueeRef.current) {
      observer.observe(marqueeRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={marqueeRef} className={`relative overflow-hidden ${className}`}>
      <motion.div
        initial={{ x: reverse ? "-100%" : "0%" }}
        animate={isVisible ? { x: reverse ? "0%" : "-100%" } : { x: reverse ? "-100%" : "0%" }}
        transition={isVisible ? {
          duration: 30,
          repeat: Infinity,
          repeatType: "loop",
          ease: "linear",
        } : { duration: 0 }}
        className="flex whitespace-nowrap"
        style={{
          // Safari optimizations
          transform: 'translateZ(0)',
          willChange: isVisible ? 'transform' : 'auto'
        }}
      >
        <div className="flex">{children}</div>
        <div className="flex">{children}</div>
      </motion.div>
    </div>
  );
};

export function TemplatesLanding() {
  const [showVideo, setShowVideo] = useState(false); // Default to static templates
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [squareVideoTemplates, setSquareVideoTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState(null);

  // Fetch video templates only when user clicks on video toggle
  useEffect(() => {
    const fetchVideoTemplates = async () => {
      try {
        setIsLoadingVideo(true);
        const response = await getPublicVideoTemplates();
        
        // The API returns { videoTemplates: [...] } - already filtered for square format
        const videoTemplatesList = response.videoTemplates || [];
        setVideoTemplates(videoTemplatesList);
        
        // Since API now only returns square format templates, use them directly
        setSquareVideoTemplates(videoTemplatesList);
      } catch (error) {
        console.error('Error fetching video templates:', error);
      } finally {
        setIsLoadingVideo(false);
      }
    };

    // Only fetch when user wants to see video templates
    if (showVideo && videoTemplates.length === 0) {
      fetchVideoTemplates();
    }
  }, [showVideo]);

  // Shuffle array function for randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Duplicate video templates to ensure smooth infinite scroll
  const duplicateVideoTemplates = (templates, minCount = 12) => {
    if (templates.length === 0) return [];
    
    const duplicated = [];
    const timesToDuplicate = Math.max(1, Math.ceil(minCount / templates.length));
    
    for (let i = 0; i < timesToDuplicate; i++) {
      duplicated.push(...templates);
    }
    
    return duplicated;
  };

  // Split video templates into two distinct groups (no overlap)
  const splitVideoTemplates = (templates) => {
    if (templates.length === 0) return { firstRow: [], secondRow: [] };
    
    // Shuffle the templates first for randomization
    const shuffled = shuffleArray(templates);
    
    // Split roughly in half, but ensure each row has at least 3 templates minimum
    const minPerRow = Math.min(3, Math.floor(shuffled.length / 2));
    const splitPoint = Math.max(minPerRow, Math.floor(shuffled.length / 2));
    
    const firstRowTemplates = shuffled.slice(0, splitPoint);
    const secondRowTemplates = shuffled.slice(splitPoint);
    
    // If second row is empty or too small, give it some templates
    if (secondRowTemplates.length === 0 && firstRowTemplates.length > 1) {
      const half = Math.floor(firstRowTemplates.length / 2);
      return {
        firstRow: firstRowTemplates.slice(0, half),
        secondRow: firstRowTemplates.slice(half)
      };
    }
    
    return {
      firstRow: firstRowTemplates,
      secondRow: secondRowTemplates
    };
  };

  const { firstRow: firstRowTemplates, secondRow: secondRowTemplates } = splitVideoTemplates(squareVideoTemplates);
  
  // Duplicate each row separately to ensure smooth infinite scroll
  const firstRowVideos = duplicateVideoTemplates(firstRowTemplates);
  const secondRowVideos = duplicateVideoTemplates(secondRowTemplates);

  // Modal functions
  const openModal = (videoTemplate) => {
    setSelectedVideoTemplate(videoTemplate);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoTemplate(null);
  };

  // Convert Firebase Storage URLs for video playback
  const convertFirebaseStorageUrl = (gsUrl) => {
    if (!gsUrl) return gsUrl;
    
    // If it's already an HTTP URL (signed URL from backend), return as-is
    if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
      return gsUrl;
    }
    
    // Only convert gs:// URLs (for backward compatibility)
    if (!gsUrl.startsWith('gs://')) {
      return gsUrl; // Return as-is if not a gs:// URL
    }
    
    // Extract bucket and path from gs://bucket/path
    const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
    if (!match) {
      return gsUrl; // Return as-is if format doesn't match
    }
    
    const [, bucket, path] = match;
    // Convert to Firebase Storage HTTP URL
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  };

  return (
    <section id="templates" className="relative bg-white dark:bg-gray-900 px-6 py-24 sm:py-32 lg:px-8 z-10 scroll-mt-40 no-scroll-anchor">
      {/* Background overlay to cover the hero marquee */}
      <div className="absolute inset-0 bg-white dark:bg-gray-900 z-0"></div>
      
      <div className="mx-auto max-w-3xl sm:max-w-4xl text-center relative z-10">
        <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Template Library</h2>
        <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
          No more guessing what works in advocacy creative
        </p>
        <p className="mt-6 text-lg text-gray-600 dark:text-gray-400 mx-auto text-center max-w-[90%]">
          Every template in Advocacy Lab is inspired by a real, high-performing ad—no fluff, no filler. We sift through thousands of ads so you don't have to.
        </p>
      </div>

      {/* Toggle Button */}
      <div className="mx-auto mt-12 max-w-7xl relative z-10">
        <ToggleButton isVideo={showVideo} onChange={setShowVideo} />
      </div>
      
      <div className="mx-auto mt-8 max-w-7xl relative z-10">
        <div className="relative w-full overflow-hidden">
          {showVideo ? (
            // Video Templates Marquee
            isLoadingVideo ? (
              <>
                <Marquee className="mb-6">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <VideoSkeletonCard key={`skeleton-first-${index}`} />
                  ))}
                </Marquee>
                
                <Marquee reverse className="mb-6">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <VideoSkeletonCard key={`skeleton-second-${index}`} />
                  ))}
                </Marquee>
              </>
            ) : squareVideoTemplates.length > 0 ? (
              <>
                <Marquee className="mb-6">
                  {firstRowVideos.map((videoTemplate, index) => (
                    <VideoTemplateCard key={`video-first-${videoTemplate.id || videoTemplate.title || 'unknown'}-${index}`} videoTemplate={videoTemplate} onPlay={openModal} />
                  ))}
                </Marquee>
                
                <Marquee reverse className="mb-6">
                  {secondRowVideos.map((videoTemplate, index) => (
                    <VideoTemplateCard key={`video-second-${videoTemplate.id || videoTemplate.title || 'unknown'}-${index}`} videoTemplate={videoTemplate} onPlay={openModal} />
                  ))}
                </Marquee>
              </>
            ) : (
              <div className="flex items-center justify-center py-16">
                <p className="text-gray-600 dark:text-gray-400">No square video templates available</p>
              </div>
            )
          ) : (
            // Static Templates Marquee (existing)
            <>
              <Marquee className="mb-6">
                {firstRow.map((image, index) => (
                  <TemplateCard key={`first-${index}`} image={image} />
                ))}
              </Marquee>
              
              <Marquee reverse className="mb-6">
                {secondRow.map((image, index) => (
                  <TemplateCard key={`second-${index}`} image={image} />
                ))}
              </Marquee>
            </>
          )}
          
          {/* Gradient fades at the edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1/6 bg-gradient-to-r from-white dark:from-gray-900 z-20"></div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-1/6 bg-gradient-to-l from-white dark:from-gray-900 z-20"></div>
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className="mx-auto mt-8 mb-8 max-w-4xl px-6 text-center relative z-10">
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          Templates based on publicly available designs used in real political campaigns and advertisements. The inclusion of these designs does not imply any affiliation, partnership, or connection with the original campaign, candidate, or organization. It does not constitute an endorsement of our services by them, nor does it grant any right or license to use their original branding, images, or other copyrighted materials, which remain the property of their respective owners.
        </p>
      </div>
      
      {/* View all templates link - with more prominent styling */}
      <div className="mt-12 text-center">
        <Link 
          to="/demo-templates"
          className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
        >
          See templates
          <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {/* Video Template Modal */}
      {isModalOpen && selectedVideoTemplate && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/50 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in-95 duration-200 ease-out w-[95%] md:w-full mx-auto">
            <div className="relative bg-white dark:bg-gray-900 rounded-xl md:rounded-2xl shadow-xl w-full max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[80vh] md:max-h-[90vh] overflow-hidden mx-auto my-1 md:my-8">
              {/* Close button */}
              <button 
                onClick={closeModal}
                className="absolute top-2 right-2 md:top-4 md:right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
                aria-label="Close"
              >
                <X className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
              </button>

              {/* Content with side-by-side layout */}
              <div className="flex flex-col lg:flex-row h-full">
                {/* Left side - Video */}
                <div className="lg:w-1/2 flex-shrink-0 flex items-center justify-center p-6 md:p-8 lg:p-12 relative">
                  <div className="relative w-full">
                    <video
                      src={convertFirebaseStorageUrl(selectedVideoTemplate.videoUrl)}
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-auto max-h-[35vh] md:max-h-[50vh] lg:max-h-[65vh] object-contain rounded-lg shadow-lg"
                      style={{ aspectRatio: '16/9' }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>

                {/* Right side - Template details */}
                <div className="lg:w-1/2 flex-1 overflow-auto border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 max-h-[50vh] lg:max-h-none">
                  <div className="p-4 md:p-4 lg:p-10">
                    <div className="flex flex-col">
                      <div className="space-y-4 md:space-y-6 flex-grow text-sm md:text-base">
                        {/* Blurred Info Items */}
                        <div className="space-y-4 md:space-y-6 mb-4 md:mb-6 filter blur-[2px] pointer-events-none">
                          {/* Title field */}
                          {selectedVideoTemplate.title && (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                              <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Title:</div>
                              <div className="flex-1 text-gray-800 dark:text-white break-words font-medium">
                                {selectedVideoTemplate.title}
                              </div>
                            </div>
                          )}

                          {/* Creator field */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                            <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Creator:</div>
                            <div className="flex-1 text-gray-800 dark:text-white break-words">
                              Advocacy Lab
                            </div>
                          </div>

                          {/* Added Date */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                            <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Added:</div>
                            <div className="flex-1 flex items-center text-gray-800 dark:text-white">
                              <span>Recently</span>
                            </div>
                          </div>
                        </div>

                        {/* Account required notice */}
                        <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800/30">
                          <p className="text-sm text-purple-800 dark:text-purple-300">
                            Sign up for an account to access full template details and editing capabilities.
                          </p>
                        </div>
                      </div>
                      
                      {/* Footer button */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 md:pt-6 mt-4 md:mt-6">
                        <div className="flex flex-col space-y-3 pb-5 sm:pb-4 md:pb-0">
                          <Link
                            to="/signup"
                            className="w-full py-2.5 md:py-3 px-3 md:px-4 rounded-md text-sm md:text-base font-medium text-white transition-colors flex items-center justify-center hover:shadow-lg"
                            style={{
                              background: "linear-gradient(45deg, #00C4CC, #7D2AE8)",
                              boxShadow: "0 2px 6px rgba(0, 196, 204, 0.3)",
                              transition: "box-shadow 0.3s ease, filter 0.3s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "linear-gradient(45deg, #00D4DC, #8D3AF8)";
                              e.currentTarget.style.filter = "brightness(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "linear-gradient(45deg, #00C4CC, #7D2AE8)";
                              e.currentTarget.style.filter = "none";
                            }}
                          >
                            Sign Up to Edit in Canva
                          </Link>
                          
                          {/* Close button - only visible on mobile */}
                          <button
                            onClick={closeModal}
                            className="w-full py-2.5 px-3 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors lg:hidden mb-2 sm:mb-3 md:mb-4"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
} 